import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import {
  catchError,
  concatMap,
  EMPTY,
  exhaustMap,
  filter,
  interval,
  map,
  mergeMap,
  MonoTypeOperatorFunction,
  Observable,
  of,
  switchMap,
  take,
  takeUntil,
  takeWhile,
  tap,
} from 'rxjs';
import { match, P } from 'ts-pattern';

import { APP_ROUTES } from '@ps/core-config';
import { EffectsBase, fromSocketEvent } from '@ps/core-data-access';
import { ToastService } from '@ps/core-overlay';

import {
  SurveyService,
  PsidService,
  ReconciliationService,
  SurveyActivityLogService,
  SurveyCommentsService,
  SurveyListActions,
  SURVEY_SOCKET_EVENTS,
} from '@ps/survey-data-access';
import {
  SurveyChannelCreationConfirmedEvent,
  UploadPsidFileResult,
} from '@ps/survey-utils';

import {
  ManageSurveyActions,
  ManageSurveyApiActions,
  ManageSurveySiblingActions,
  ManageSurveySiblingApiActions,
} from './manage-survey.actions';
import {
  selectQuotaLoadedPage,
  selectQuotaSearch,
} from './manage-survey.selectors';
import {
  PSID_UPLOAD_MESSAGES,
  psidUploadedMessage,
  TRAFFIC_CHANNEL_ERROR_MESSAGES,
  TRAFFIC_CHANNEL_POLL_INTERVAL_MS,
  TRAFFIC_CHANNEL_POLL_MAX_ATTEMPTS,
} from './manage-survey-traffic-channel.constants';
import { hasPendingChannel } from './manage-survey-traffic-channel.helper';

@Injectable()
export class ManageSurveyEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store);
  private readonly _surveyService = inject(SurveyService);
  private readonly _psidService = inject(PsidService);
  private readonly _activityLogService = inject(SurveyActivityLogService);
  private readonly _commentsService = inject(SurveyCommentsService);
  private readonly _reconciliationService = inject(ReconciliationService);
  private readonly _toast = inject(ToastService);
  private readonly _router = inject(Router);

  // Handles both the lightweight preview-drawer load (`loadSurveyDetails`,
  // plain `getById`) and the full Manage Survey page load
  // (`loadFullSurveyDetails`, `getByIdTargeting`) — same success/failure
  // actions either way, so one effect picks the right service call per
  // action instead of duplicating the pipeline.
  public loadSurvey$ = createEffect(() =>
    this._actions$.pipe(
      ofType(
        ManageSurveyActions.loadSurveyDetails,
        ManageSurveyActions.loadFullSurveyDetails,
      ),
      switchMap((action) => {
        const survey$ =
          action.type === ManageSurveyActions.loadFullSurveyDetails.type
            ? this._surveyService.getByIdTargeting(action.surveyId)
            : this._surveyService.getById(action.surveyId);

        return survey$.pipe(
          map((survey) => ManageSurveyApiActions.loadSurveySuccess({ survey })),
          this.psCatchError(
            (err) =>
              of(
                ManageSurveyApiActions.loadSurveyFailure({
                  error: err.message ?? 'Failed to load survey',
                }),
              ),
            { notify: true },
          ),
        );
      }),
    ),
  );

  public loadChildSurvey$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.loadChildSurveyDetails),
      switchMap(({ surveyId }) =>
        this._surveyService.getById(surveyId, 0, true, 'page').pipe(
          map((survey) =>
            ManageSurveyApiActions.loadChildSurveySuccess({ survey }),
          ),
          this.psCatchError(
            (err) =>
              of(
                ManageSurveyApiActions.loadChildSurveyFailure({
                  error: err.message ?? 'Failed to load survey',
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public loadSurveyHealth$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.loadSurveyHealth),
      switchMap(({ surveyId }) =>
        this._surveyService.getHealth(surveyId).pipe(
          map((healthMetrics) =>
            ManageSurveyApiActions.loadSurveyHealthSuccess({ healthMetrics }),
          ),
          this.psCatchError(
            (err) =>
              of(
                ManageSurveyApiActions.loadSurveyHealthFailure({
                  error: err.message ?? 'Failed to load survey health',
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public loadRemainingHours$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.loadRemainingHours),
      switchMap(({ surveyId }) =>
        this._surveyService.getRemainingHours(surveyId).pipe(
          map((remainingHours) =>
            ManageSurveyApiActions.loadRemainingHoursSuccess({
              remainingHours,
            }),
          ),
          this.psCatchError(
            (err) =>
              of(
                ManageSurveyApiActions.loadRemainingHoursFailure({
                  error: err.message ?? 'Failed to load remaining hours',
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public loadReconciliationAttempts$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.loadReconciliationAttempts),
      switchMap(({ surveyId }) =>
        this._reconciliationService.getAttemptsForSurvey(surveyId).pipe(
          map((reconciliationAttempts) =>
            ManageSurveyApiActions.loadReconciliationAttemptsSuccess({
              reconciliationAttempts,
            }),
          ),
          this.psCatchError(
            (err) =>
              of(
                ManageSurveyApiActions.loadReconciliationAttemptsFailure({
                  error:
                    err.message ?? 'Failed to load reconciliation attempts',
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public loadActivityLogs$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.loadActivityLogs),
      switchMap(({ surveyId }) =>
        this._activityLogService.getBySurveyId(surveyId).pipe(
          map((activityLogs) =>
            ManageSurveyApiActions.loadActivityLogsSuccess({ activityLogs }),
          ),
          this.psCatchError(
            (err) =>
              of(
                ManageSurveyApiActions.loadActivityLogsFailure({
                  error: err.message ?? 'Failed to load activity logs',
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public loadComments$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.loadComments),
      switchMap(({ surveyId }) =>
        this._commentsService.commentsForSurvey(surveyId).pipe(
          map((comments) =>
            ManageSurveyApiActions.loadCommentsSuccess({ comments }),
          ),
          this.psCatchError((error) =>
            of(
              ManageSurveyApiActions.loadCommentsFailure({
                error: error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public addComment$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.addComment),
      switchMap(({ surveyId, body }) =>
        this._commentsService.createComment(surveyId, body).pipe(
          map((comment) =>
            ManageSurveyApiActions.addCommentSuccess({ comment }),
          ),
          this.psCatchError((error) =>
            of(
              ManageSurveyApiActions.addCommentFailure({
                error: error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public loadSurveyQuotas$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.loadSurveyQuotas),
      switchMap(({ surveyId, page, search }) =>
        this._surveyService.getQuotas(surveyId, page, search, 'none').pipe(
          map(({ quotas, total }) =>
            ManageSurveyApiActions.loadSurveyQuotasSuccess({ quotas, total }),
          ),
          this.psCatchError(
            (err) =>
              of(
                ManageSurveyApiActions.loadSurveyQuotasFailure({
                  error: err.message ?? 'Failed to load quotas',
                }),
              ),
            { notify: true },
          ),
          this._untilQuotasReset(),
        ),
      ),
    ),
  );

  public readonly updateCurrentCpi$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveySiblingActions.updateCurrentCpi),
      switchMap(({ surveyId, cpi, isChild }) =>
        this._surveyService
          .updateCurrentCpi(surveyId, cpi, isChild, 'drawer')
          .pipe(
            // Sibling-survey tables (e.g. the Multi Country drawer) key off
            // childSurveysMap, not the single active childSurvey slot, so a
            // row-level edit also broadcasts a generic patch — same
            // dual-dispatch idiom survey-socket.effects.ts uses for live ticks.
            mergeMap((survey) =>
              of(
                isChild
                  ? ManageSurveySiblingApiActions.updateChildCurrentCpiSuccess({
                      survey,
                    })
                  : ManageSurveySiblingApiActions.updateCurrentCpiSuccess({
                      survey,
                    }),
                ManageSurveyActions.updateSurvey({ surveyId, survey }),
              ),
            ),
            this.psCatchError(
              (err) =>
                of(
                  ManageSurveySiblingApiActions.updateCurrentCpiFailure({
                    error: err.message ?? 'Failed to update CPI',
                  }),
                ),
              { notify: true },
            ),
          ),
      ),
    ),
  );

  public readonly updateCompletesRequired$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveySiblingActions.updateCompletesRequired),
      // concatMap, not switchMap: a second edit landing before the first
      // PATCH resolves must queue behind it, not cancel it — switchMap would
      // silently drop the in-flight save.
      concatMap(({ surveyId, completesRequired, isChild }) =>
        this._surveyService
          .updateCompletesRequired(
            surveyId,
            completesRequired,
            isChild,
            'drawer',
          )
          .pipe(
            // Same dual-dispatch as updateCurrentCpi$ — see comment there.
            mergeMap((survey) =>
              of(
                isChild
                  ? ManageSurveySiblingApiActions.updateChildCompletesRequiredSuccess(
                      { survey },
                    )
                  : ManageSurveySiblingApiActions.updateCompletesRequiredSuccess(
                      { survey },
                    ),
                ManageSurveyActions.updateSurvey({ surveyId, survey }),
              ),
            ),
            this.psCatchError(
              (err) =>
                of(
                  ManageSurveySiblingApiActions.updateCompletesRequiredFailure({
                    error: err.message ?? 'Failed to update completes',
                  }),
                ),
              { notify: true },
            ),
          ),
      ),
    ),
  );

  public readonly updateScheduleLaunch$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveySiblingActions.updateScheduleLaunch),
      // concatMap, not switchMap — see updateCompletesRequired$ above.
      concatMap(({ surveyId, scheduleLaunch, isChild }) =>
        this._surveyService
          .updateScheduleLaunch(surveyId, scheduleLaunch, isChild, 'drawer')
          .pipe(
            // Same dual-dispatch as updateCurrentCpi$ — see comment there.
            mergeMap((survey) =>
              of(
                isChild
                  ? ManageSurveySiblingApiActions.updateChildScheduleLaunchSuccess(
                      { survey },
                    )
                  : ManageSurveySiblingApiActions.updateScheduleLaunchSuccess({
                      survey,
                    }),
                ManageSurveyActions.updateSurvey({ surveyId, survey }),
              ),
            ),
            this.psCatchError(
              (err) =>
                of(
                  ManageSurveySiblingApiActions.updateScheduleLaunchFailure({
                    error: err.message ?? 'Failed to update launch date',
                  }),
                ),
              { notify: true },
            ),
          ),
      ),
    ),
  );

  public loadMoreQuotas$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.loadMoreQuotas),
      concatLatestFrom(() => [
        this._store.select(selectQuotaLoadedPage),
        this._store.select(selectQuotaSearch),
      ]),
      switchMap(([{ surveyId }, page, search]) =>
        this._surveyService.getQuotas(surveyId, page, search).pipe(
          map(({ quotas }) =>
            ManageSurveyApiActions.loadMoreQuotasSuccess({ quotas }),
          ),
          this.psCatchError(
            () => of(ManageSurveyApiActions.loadMoreQuotasFailure()),
            { notify: true },
          ),
          this._untilQuotasReset(),
        ),
      ),
    ),
  );

  public loadAllQuotas$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.loadAllQuotas),
      concatLatestFrom(() => this._store.select(selectQuotaSearch)),
      switchMap(([{ surveyId }, search]) =>
        this._surveyService.getQuotas(surveyId, undefined, search).pipe(
          map(({ quotas }) =>
            ManageSurveyApiActions.loadAllQuotasSuccess({ quotas }),
          ),
          this.psCatchError(
            () => of(ManageSurveyApiActions.loadAllQuotasFailure()),
            { notify: true },
          ),
          this._untilQuotasReset(),
        ),
      ),
    ),
  );

  public setQuotaLock$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.setQuotaLock),
      mergeMap(({ surveyId, psQuotaId, locked }) =>
        this._surveyService.setQuotaLock(surveyId, psQuotaId, locked).pipe(
          map(() =>
            ManageSurveyApiActions.setQuotaLockSuccess({ psQuotaId, locked }),
          ),
          this.psCatchError(
            (err) =>
              of(
                ManageSurveyApiActions.setQuotaLockFailure({
                  psQuotaId,
                  error: err.message ?? 'Failed to update quota lock',
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public setQuotaCurrentTarget$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.setQuotaCurrentTarget),
      mergeMap(({ surveyId, psQuotaId, currentTarget, updateSupplier }) =>
        this._surveyService
          .setQuotaCurrentTarget(
            surveyId,
            psQuotaId,
            currentTarget,
            updateSupplier,
          )
          .pipe(
            map((update) =>
              ManageSurveyApiActions.setQuotaCurrentTargetSuccess({
                psQuotaId,
                update,
              }),
            ),
            this.psCatchError(
              (err) =>
                of(
                  ManageSurveyApiActions.setQuotaCurrentTargetFailure({
                    psQuotaId,
                    error: err.message ?? 'Failed to update quota target',
                  }),
                ),
              { notify: true },
            ),
          ),
      ),
    ),
  );

  public setQuotaCurrentlyOpen$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.setQuotaCurrentlyOpen),
      mergeMap(({ surveyId, psQuotaId, currentlyOpen }) =>
        this._surveyService
          .setQuotaCurrentlyOpen(surveyId, psQuotaId, currentlyOpen)
          .pipe(
            map((update) =>
              ManageSurveyApiActions.setQuotaCurrentlyOpenSuccess({
                psQuotaId,
                update,
              }),
            ),
            this.psCatchError(
              (err) =>
                of(
                  ManageSurveyApiActions.setQuotaCurrentlyOpenFailure({
                    psQuotaId,
                    error: err.message ?? 'Failed to update currently open',
                  }),
                ),
              { notify: true },
            ),
          ),
      ),
    ),
  );

  public setSupplierQuotaCurrentTarget$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.setSupplierQuotaCurrentTarget),
      mergeMap(({ surveyId, psQuotaId, supplierRefId, currentTarget }) =>
        this._surveyService
          .setSupplierQuotaField(
            surveyId,
            psQuotaId,
            supplierRefId,
            'currentTarget',
            currentTarget,
          )
          .pipe(
            map(() =>
              ManageSurveyApiActions.setSupplierQuotaCurrentTargetSuccess({
                psQuotaId,
                supplierRefId,
                currentTarget,
              }),
            ),
            this.psCatchError(
              (err) =>
                of(
                  ManageSurveyApiActions.setSupplierQuotaCurrentTargetFailure({
                    psQuotaId,
                    supplierRefId,
                    error: err.message ?? 'Failed to update supplier target',
                  }),
                ),
              { notify: true },
            ),
          ),
      ),
    ),
  );

  public setSupplierQuotaCurrentlyOpen$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.setSupplierQuotaCurrentlyOpen),
      mergeMap(({ surveyId, psQuotaId, supplierRefId, currentlyOpen }) =>
        this._surveyService
          .setSupplierQuotaField(
            surveyId,
            psQuotaId,
            supplierRefId,
            'currentlyOpen',
            currentlyOpen,
          )
          .pipe(
            map(() =>
              ManageSurveyApiActions.setSupplierQuotaCurrentlyOpenSuccess({
                psQuotaId,
                supplierRefId,
                currentlyOpen,
              }),
            ),
            this.psCatchError(
              (err) =>
                of(
                  ManageSurveyApiActions.setSupplierQuotaCurrentlyOpenFailure({
                    psQuotaId,
                    supplierRefId,
                    error:
                      err.message ?? 'Failed to update supplier currently open',
                  }),
                ),
              { notify: true },
            ),
          ),
      ),
    ),
  );

  public renameQuota$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.renameQuota),
      mergeMap(({ surveyId, psQuotaId, label }) =>
        this._surveyService.renameQuota(surveyId, psQuotaId, label).pipe(
          map(() =>
            ManageSurveyApiActions.renameQuotaSuccess({ psQuotaId, label }),
          ),
          this.psCatchError(
            (err) =>
              of(
                ManageSurveyApiActions.renameQuotaFailure({
                  psQuotaId,
                  error: err.message ?? 'Failed to rename quota',
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public setQuotaCpi$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.setQuotaCpi),
      mergeMap(({ surveyId, psQuotaId, cpi }) =>
        this._surveyService.setQuotaCpi(surveyId, psQuotaId, cpi).pipe(
          map(() =>
            ManageSurveyApiActions.setQuotaCpiSuccess({
              surveyId,
              psQuotaId,
              cpi,
            }),
          ),
          this.psCatchError(
            (err) =>
              of(
                ManageSurveyApiActions.setQuotaCpiFailure({
                  psQuotaId,
                  error: err.message ?? 'Failed to update quota CPI',
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  // Fire-and-forget analytics ping — mirrors legacy's non-blocking
  // pushApplyPriceRecoButtonCount call, kept as its own effect so a change to
  // one concern never risks the other; errors here must never surface to the
  // store, since the CPI update itself already succeeded.
  public pushApplyPriceRecoButtonCount$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(ManageSurveyApiActions.setQuotaCpiSuccess),
        mergeMap(({ surveyId }) =>
          this._surveyService
            .pushApplyPriceRecoButtonCount(surveyId)
            .pipe(catchError(() => EMPTY)),
        ),
      ),
    { dispatch: false },
  );

  // Traffic channels are a manage-survey-only concept, so this listens for
  // the socket event directly rather than relaying it in from data-access.
  public trafficChannelUpdates$ = createEffect(() =>
    fromSocketEvent<number>(SURVEY_SOCKET_EVENTS.TRAFFIC_CHANNEL_UPDATES).pipe(
      map((surveyId) => ManageSurveyActions.loadTrafficChannels({ surveyId })),
    ),
  );

  public loadTrafficChannels$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.loadTrafficChannels),
      switchMap(({ surveyId }) =>
        this._surveyService.getTrafficChannelChildren(surveyId).pipe(
          map((trafficChannels) =>
            ManageSurveyApiActions.loadTrafficChannelsSuccess({
              trafficChannels,
            }),
          ),
          this.psCatchError(
            (err) =>
              of(
                ManageSurveyApiActions.loadTrafficChannelsFailure({
                  error: err.message ?? TRAFFIC_CHANNEL_ERROR_MESSAGES.LOAD,
                }),
              ),
            {
              notify: true,
              fallbackMessage: TRAFFIC_CHANNEL_ERROR_MESSAGES.LOAD,
            },
          ),
        ),
      ),
    ),
  );

  public setTrafficChannelStatus$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.setTrafficChannelStatus),
      mergeMap(({ surveyId, status }) =>
        this._surveyService.updateSurveyStatus(surveyId, status).pipe(
          map(() =>
            ManageSurveyApiActions.setTrafficChannelStatusSuccess({
              surveyId,
              status,
            }),
          ),
          this.psCatchError(
            (err) =>
              of(
                ManageSurveyApiActions.setTrafficChannelStatusFailure({
                  surveyId,
                  error:
                    err.message ?? TRAFFIC_CHANNEL_ERROR_MESSAGES.STATUS_UPDATE,
                }),
              ),
            {
              notify: true,
              fallbackMessage: TRAFFIC_CHANNEL_ERROR_MESSAGES.STATUS_UPDATE,
            },
          ),
        ),
      ),
    ),
  );

  public updateTrafficChannelCpi$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.updateTrafficChannelCpi),
      mergeMap(({ surveyId, cpi }) =>
        this._surveyService.updateTrafficChannelCpi(surveyId, cpi).pipe(
          map((savedCpi) =>
            ManageSurveyApiActions.updateTrafficChannelCpiSuccess({
              surveyId,
              cpi: savedCpi,
            }),
          ),
          this.psCatchError(
            (err) =>
              of(
                ManageSurveyApiActions.updateTrafficChannelCpiFailure({
                  surveyId,
                  error:
                    err.message ?? TRAFFIC_CHANNEL_ERROR_MESSAGES.CPI_UPDATE,
                }),
              ),
            {
              notify: true,
              fallbackMessage: TRAFFIC_CHANNEL_ERROR_MESSAGES.CPI_UPDATE,
            },
          ),
        ),
      ),
    ),
  );

  // After a launch, re-fetch the list silently until no channel is PENDING
  // (each ends READY or ERROR); stops early when the drawer resets the list.
  public pollPendingTrafficChannels$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyApiActions.launchTrafficChannelSuccess),
      filter(({ trafficChannels }) => hasPendingChannel(trafficChannels)),
      switchMap(({ surveyId }) => this._pollTrafficChannels(surveyId)),
    ),
  );

  // A launched Specific Audience channel still needs its audience before it
  // goes live, so the user goes straight to its Edit Survey page.
  public navigateToSpecificAudienceEditor$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(ManageSurveyApiActions.launchSpecificAudienceSuccess),
        tap(
          ({ channelId }) =>
            void this._router.navigate([APP_ROUTES.editSurvey(channelId)]),
        ),
      ),
    { dispatch: false },
  );

  public launchTrafficChannel$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.launchTrafficChannel),
      mergeMap(({ surveyId, event }) =>
        this._launchTrafficChannel(surveyId, event).pipe(
          this.psCatchError(
            (err) =>
              of(
                ManageSurveyApiActions.launchTrafficChannelFailure({
                  surveyId,
                  error: err.message ?? TRAFFIC_CHANNEL_ERROR_MESSAGES.LAUNCH,
                }),
              ),
            {
              notify: true,
              fallbackMessage: TRAFFIC_CHANNEL_ERROR_MESSAGES.LAUNCH,
            },
          ),
        ),
      ),
    ),
  );

  public uploadPsidFile$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.uploadPsidFile),
      switchMap(({ surveyId, file }) =>
        this._psidService.uploadPsidFile(surveyId, file).pipe(
          map((result) => this._uploadPsidFileOutcome(result)),
          this.psCatchError(
            (err) =>
              of(
                ManageSurveyApiActions.uploadPsidFileFailure({
                  error: err.message ?? PSID_UPLOAD_MESSAGES.GENERIC_ERROR,
                }),
              ),
            {
              notify: true,
              fallbackMessage: PSID_UPLOAD_MESSAGES.GENERIC_ERROR,
            },
          ),
        ),
      ),
    ),
  );

  public removePsidFile$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.removePsidFile),
      switchMap(({ filePath }) =>
        this._psidService.removePsidFile(filePath).pipe(
          tap(() =>
            this._toast.success(PSID_UPLOAD_MESSAGES.CLEARED, {
              keepOpen: false,
            }),
          ),
          map(() => ManageSurveyApiActions.removePsidFileSuccess()),
          this.psCatchError(
            (err) =>
              of(
                ManageSurveyApiActions.removePsidFileFailure({
                  error: err.message ?? PSID_UPLOAD_MESSAGES.GENERIC_ERROR,
                }),
              ),
            {
              notify: true,
              fallbackMessage: PSID_UPLOAD_MESSAGES.GENERIC_ERROR,
            },
          ),
        ),
      ),
    ),
  );

  // Cleanup of an upload the user walked away from: nothing to announce, and a
  // failure leaves only an orphaned file, so neither toasts. mergeMap so a
  // quick second close does not cancel the first delete.
  public discardPsidFile$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.discardPsidFile),
      mergeMap(({ filePath }) =>
        this._psidService.discardPsidFile(filePath).pipe(
          map(() => ManageSurveyApiActions.discardPsidFileSuccess()),
          this.psCatchError(
            (err) =>
              of(
                ManageSurveyApiActions.discardPsidFileFailure({
                  error: err.message ?? PSID_UPLOAD_MESSAGES.GENERIC_ERROR,
                }),
              ),
            { notify: false },
          ),
        ),
      ),
    ),
  );

  public downloadRespondentListTemplate$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.downloadRespondentListTemplate),
      switchMap(() =>
        this._surveyService.downloadRespondentListTemplate().pipe(
          map(() =>
            ManageSurveyApiActions.downloadRespondentListTemplateSuccess(),
          ),
          this.psCatchError(
            (err) =>
              of(
                ManageSurveyApiActions.downloadRespondentListTemplateFailure({
                  error: err.message ?? 'Failed to download template',
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public saveCrmId$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.saveCrmId),
      mergeMap(({ surveyId, crmId }) =>
        this._surveyService
          .patchSurvey(surveyId, [
            { op: 'replace', path: '/crmId', value: crmId },
          ])
          .pipe(
            mergeMap(() => [
              ManageSurveyApiActions.saveCrmIdSuccess(),
              ManageSurveyActions.updateSurvey({ surveyId, survey: { crmId } }),
              SurveyListActions.updateSurvey({ surveyId, survey: { crmId } }),
            ]),
            this.psCatchError(
              () =>
                of(
                  ManageSurveyApiActions.saveCrmIdFailure({
                    error: 'Error while updating CRM ID',
                  }),
                ),
              { notify: true, customMessage: 'Error while updating CRM ID' },
            ),
          ),
      ),
    ),
  );

  public saveAsanaId$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.saveAsanaId),
      mergeMap(({ surveyId, asanaId }) =>
        this._surveyService
          .patchSurvey(surveyId, [
            { op: 'replace', path: '/asanaId', value: asanaId },
          ])
          .pipe(
            mergeMap(() => [
              ManageSurveyApiActions.saveAsanaIdSuccess(),
              ManageSurveyActions.updateSurvey({
                surveyId,
                survey: { asanaId },
              }),
            ]),
            this.psCatchError(
              () =>
                of(
                  ManageSurveyApiActions.saveAsanaIdFailure({
                    error: 'Error while updating ASANA ID',
                  }),
                ),
              { notify: true, customMessage: 'Error while updating ASANA ID' },
            ),
          ),
      ),
    ),
  );

  public saveBillingNumber$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.saveBillingNumber),
      mergeMap(({ surveyId, billingNumber }) =>
        this._surveyService
          .patchSurvey(surveyId, [
            { op: 'replace', path: '/billing_id', value: billingNumber },
          ])
          .pipe(
            mergeMap(() => [
              ManageSurveyApiActions.saveBillingNumberSuccess(),
              ManageSurveyActions.updateSurvey({
                surveyId,
                survey: { billingId: billingNumber },
              }),
              SurveyListActions.updateSurvey({
                surveyId,
                survey: { billingId: billingNumber },
              }),
            ]),
            this.psCatchError(
              () =>
                of(
                  ManageSurveyApiActions.saveBillingNumberFailure({
                    error: 'Error while updating Billing Number',
                  }),
                ),
              {
                notify: true,
                customMessage: 'Error while updating Billing Number',
              },
            ),
          ),
      ),
    ),
  );

  public saveCustomRedirectField$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSurveyActions.saveCustomRedirectField),
      mergeMap(({ surveyId, value }) =>
        this._surveyService.updateCustomRedirectField(surveyId, value).pipe(
          tap(() =>
            this._toast.success(
              'Success! Custom Redirect Field has been updated.',
              { keepOpen: false },
            ),
          ),
          mergeMap(() => [
            ManageSurveyApiActions.saveCustomRedirectFieldSuccess({ value }),
            ManageSurveyActions.updateSurvey({
              surveyId,
              survey: { supplierCustomRedirectField: value },
            }),
          ]),
          this.psCatchError(
            (err) =>
              of(
                ManageSurveyApiActions.saveCustomRedirectFieldFailure({
                  error:
                    err.message ?? 'Error while updating Custom Redirect Field',
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  // Replica and Targeted List answer with the refreshed list; Specific Audience
  // only with the new channel's id.
  private _launchTrafficChannel(
    surveyId: number,
    event: SurveyChannelCreationConfirmedEvent,
  ): Observable<Action> {
    return match(event)
      .with({ cardType: 'traffic-channel' }, (specificAudienceEvent) =>
        this._surveyService
          .launchSpecificAudience(surveyId, specificAudienceEvent)
          .pipe(
            map((channelId) =>
              ManageSurveyApiActions.launchSpecificAudienceSuccess({
                surveyId,
                channelId,
              }),
            ),
          ),
      )
      .with({ cardType: P.union('replica', 'targeted-list') }, (listEvent) =>
        this._surveyService.launchTrafficChannel(surveyId, listEvent).pipe(
          map((trafficChannels) =>
            ManageSurveyApiActions.launchTrafficChannelSuccess({
              surveyId,
              trafficChannels,
            }),
          ),
        ),
      )
      .exhaustive();
  }

  private _pollTrafficChannels(surveyId: number): Observable<Action> {
    return interval(TRAFFIC_CHANNEL_POLL_INTERVAL_MS).pipe(
      take(TRAFFIC_CHANNEL_POLL_MAX_ATTEMPTS),
      exhaustMap(() =>
        this._surveyService.pollTrafficChannelChildren(surveyId),
      ),
      takeWhile(hasPendingChannel, true),
      map((trafficChannels) =>
        ManageSurveyApiActions.loadTrafficChannelsSuccess({ trafficChannels }),
      ),
      takeUntil(
        this._actions$.pipe(ofType(ManageSurveyActions.resetTrafficChannels)),
      ),
      this.psCatchError(
        (err) =>
          of(
            ManageSurveyApiActions.pollTrafficChannelsFailure({
              error: err.message ?? TRAFFIC_CHANNEL_ERROR_MESSAGES.LOAD,
            }),
          ),
        {
          notify: true,
          fallbackMessage: TRAFFIC_CHANNEL_ERROR_MESSAGES.LOAD,
        },
      ),
    );
  }

  // Each quota effect has its own switchMap, so a new survey's first-page load
  // never cancels an older "Show next" / "Show all" request. Every survey switch
  // dispatches resetSurveyQuotas first, so cancelling there stops a late response
  // for the previous survey from landing in the next one's state.
  private _untilQuotasReset<T>(): MonoTypeOperatorFunction<T> {
    return takeUntil(
      this._actions$.pipe(ofType(ManageSurveyActions.resetSurveyQuotas)),
    );
  }

  // A file whose PSIDs are all invalid or duplicates uploads fine but cannot
  // target anyone, so it is treated as a failed upload.
  private _uploadPsidFileOutcome(result: UploadPsidFileResult): Action {
    if (!result.uniqPsids.length) {
      this._toast.error(PSID_UPLOAD_MESSAGES.EMPTY_UPLOAD);
      return ManageSurveyApiActions.uploadPsidFileFailure({
        error: PSID_UPLOAD_MESSAGES.EMPTY_UPLOAD,
      });
    }
    this._toast.success(
      psidUploadedMessage(result.uniqPsidCount, result.uploadedPsidCount),
      { keepOpen: false },
    );
    return ManageSurveyApiActions.uploadPsidFileSuccess(result);
  }
}
